import { useMemo, useState, useCallback } from 'react';
import { Calendar as BigCalendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  ChevronLeft,
  ChevronRight,
  AlertCircle,
} from 'lucide-react';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import type { ExtendedTask } from '@/hooks/useTasks';
import type { TaskPriority, TaskStatus } from '@/types/tasks';

// Setup date-fns localizer
const locales = {
  'en-US': enUS,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

interface CalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  allDay: boolean;
  resource: ExtendedTask;
  priority: TaskPriority;
  status: TaskStatus;
}

interface TaskCalendarProps {
  tasks: ExtendedTask[];
  onTaskClick: (task: ExtendedTask) => void;
  onAddTask?: (date: Date) => void;
  onComplete: (taskId: string) => void;
  className?: string;
}

const priorityColors: Record<TaskPriority, string> = {
  low: '#94a3b8',
  medium: '#3b82f6',
  high: '#f97316',
  urgent: '#ef4444',
};

const priorityBgColors: Record<TaskPriority, string> = {
  low: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  medium: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  high: 'bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
  urgent: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300',
};

// Type definitions for react-big-calendar
type View = 'month' | 'week' | 'day' | 'agenda';

interface EventProps<T> {
  event: T;
}

// Custom Event Component
function CustomEvent({ event }: EventProps<CalendarEvent>) {
  const task = event.resource;
  const isCompleted = task.status === 'completed';
  const isOverdue = task.status === 'overdue';

  return (
    <div
      className={cn(
        'px-2 py-1 rounded text-xs font-medium truncate cursor-pointer transition-all',
        'hover:opacity-80',
        priorityBgColors[event.priority],
        isCompleted && 'opacity-50 line-through',
        isOverdue && 'border-l-2 border-red-500'
      )}
      style={{
        borderLeftWidth: '3px',
        borderLeftColor: isCompleted ? '#22c55e' : priorityColors[event.priority],
      }}
    >
      <div className="flex items-center gap-1">
        {isOverdue && <AlertCircle className="h-3 w-3 text-red-500" />}
        <span className="truncate">{event.title}</span>
      </div>
    </div>
  );
}

// Custom Toolbar Component
interface ToolbarProps {
  date: Date;
  view: View;
  onNavigate: (action: 'PREV' | 'NEXT' | 'TODAY' | 'DATE', date?: Date) => void;
  onView: (view: View) => void;
  label: string;
}

function CustomToolbar({ date, view, onNavigate, onView, label }: ToolbarProps) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onNavigate('TODAY')}
          className="hidden sm:flex"
        >
          Today
        </Button>
        <div className="flex items-center border rounded-lg p-1 bg-muted/50">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onNavigate('PREV')}
            className="h-8 w-8"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm font-medium px-3 min-w-[140px] text-center">
            {label}
          </span>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onNavigate('NEXT')}
            className="h-8 w-8"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <div className="flex items-center border rounded-lg p-1 bg-muted/50">
          {(['month', 'week', 'day', 'agenda'] as View[]).map((v) => (
            <Button
              key={v}
              variant={view === v ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => onView(v)}
              className="capitalize"
            >
              {v}
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
}

export function TaskCalendar({
  tasks,
  onTaskClick,
  onAddTask,
  onComplete: _onComplete,
  className,
}: TaskCalendarProps) {
  const [view, setView] = useState<View>('month');
  const [date, setDate] = useState<Date>(new Date());

  // Convert tasks to calendar events
  const events = useMemo<CalendarEvent[]>(() => {
    return tasks
      .filter((task) => task.dueDate)
      .map((task) => {
        const dueDate = new Date(task.dueDate!);
        return {
          id: task.id,
          title: task.title,
          start: dueDate,
          end: dueDate,
          allDay: true,
          resource: task,
          priority: task.priority,
          status: task.status,
        };
      });
  }, [tasks]);

  const handleNavigate = useCallback((newDate: Date) => {
    setDate(newDate);
  }, []);

  const handleSelectEvent = useCallback((event: CalendarEvent) => {
    onTaskClick(event.resource);
  }, [onTaskClick]);

  const handleSelectSlot = useCallback(({ start }: { start: Date }) => {
    onAddTask?.(start);
  }, [onAddTask]);

  // Custom styles for the calendar
  const calendarStyles = `
    .rbc-calendar {
      font-family: inherit;
    }
    .rbc-header {
      padding: 12px 4px;
      font-weight: 600;
      font-size: 0.875rem;
      color: hsl(var(--muted-foreground));
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }
    .rbc-month-view {
      border-radius: 0.75rem;
      border-color: hsl(var(--border));
    }
    .rbc-month-row {
      overflow: visible;
    }
    .rbc-date-cell {
      padding: 6px 8px 2px;
      text-align: left;
      font-size: 0.8rem;
      font-weight: 500;
      color: hsl(var(--muted-foreground));
    }
    .rbc-date-cell > a {
      color: inherit;
      text-decoration: none;
      font-weight: 500;
    }
    .rbc-date-cell.rbc-now {
      color: hsl(var(--foreground));
      font-weight: 700;
    }
    .rbc-day-bg {
      transition: background-color 0.2s;
    }
    .rbc-day-bg:hover {
      background-color: hsl(var(--muted) / 0.5);
    }
    .rbc-today {
      background-color: hsl(var(--primary) / 0.05);
    }
    .rbc-off-range-bg {
      background-color: hsl(var(--muted) / 0.3);
    }
    .rbc-event {
      border: none;
      border-radius: 0.375rem;
      padding: 0;
      background: transparent;
    }
    .rbc-event-content {
      font-size: 0.75rem;
    }
    .rbc-agenda-view table {
      font-size: 0.875rem;
    }
    .rbc-agenda-view table.rbc-agenda-table thead > tr > th {
      padding: 12px;
      font-weight: 600;
    }
    .rbc-agenda-view table.rbc-agenda-table tbody > tr > td {
      padding: 12px;
    }
    .rbc-time-view {
      border-radius: 0.75rem;
    }
    .rbc-time-header-gutter,
    .rbc-time-gutter {
      width: 72px;
      min-width: 72px;
      max-width: 72px;
    }
    .rbc-time-gutter .rbc-timeslot-group,
    .rbc-time-view .rbc-timeslot-group {
      min-height: 52px;
    }
    .rbc-time-view .rbc-time-slot {
      min-height: 26px;
    }
    .rbc-time-gutter .rbc-label {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      height: 100%;
      padding-right: 10px;
      font-size: 0.75rem;
      line-height: 1;
      white-space: nowrap;
      color: hsl(var(--muted-foreground));
    }
    .rbc-time-header-content {
      border-left: none;
    }
    .rbc-time-content {
      border-top: 1px solid hsl(var(--border));
    }
    .rbc-time-slot {
      color: hsl(var(--muted-foreground));
    }
    .rbc-current-time-indicator {
      background-color: #ef4444;
      height: 2px;
    }
    .rbc-show-more {
      color: hsl(var(--primary));
      font-weight: 500;
      background: transparent;
      padding: 4px 8px;
    }

    /* Dark mode polish: softer surfaces and gentler grid contrast */
    .dark .rbc-calendar {
      color: hsl(var(--foreground));
    }
    .dark .rbc-month-view,
    .dark .rbc-time-view,
    .dark .rbc-agenda-view table {
      border-color: hsl(var(--border) / 0.65);
      background: linear-gradient(180deg, hsl(var(--card) / 0.55) 0%, hsl(var(--card) / 0.4) 100%);
    }
    .dark .rbc-header {
      color: hsl(var(--muted-foreground));
      background-color: hsl(var(--card) / 0.55);
      border-bottom: 1px solid hsl(var(--border) / 0.65);
    }
    .dark .rbc-date-cell {
      color: hsl(var(--muted-foreground));
    }
    .dark .rbc-date-cell.rbc-now {
      color: hsl(var(--foreground));
    }
    .dark .rbc-day-bg {
      background-color: hsl(var(--card) / 0.22);
    }
    .dark .rbc-day-bg:hover {
      background-color: hsl(var(--muted) / 0.35);
    }
    .dark .rbc-off-range-bg {
      background-color: hsl(var(--muted) / 0.3);
    }
    .dark .rbc-today,
    .dark .rbc-day-bg.rbc-today {
      background-color: hsl(var(--primary) / 0.14);
    }
    .dark .rbc-month-row + .rbc-month-row,
    .dark .rbc-day-bg + .rbc-day-bg,
    .dark .rbc-time-content,
    .dark .rbc-time-content > * + * > * {
      border-color: hsl(var(--border) / 0.6);
    }
    .dark .rbc-time-header-content,
    .dark .rbc-time-gutter {
      background-color: hsl(var(--card) / 0.6);
    }
    .dark .rbc-time-slot,
    .dark .rbc-time-gutter .rbc-label {
      color: hsl(var(--muted-foreground));
    }
    .dark .rbc-agenda-view table.rbc-agenda-table thead > tr > th {
      background-color: hsl(var(--card) / 0.7);
      border-bottom: 1px solid hsl(var(--border) / 0.65);
    }
    .dark .rbc-agenda-view table.rbc-agenda-table tbody > tr > td {
      border-color: hsl(var(--border) / 0.55);
      background-color: hsl(var(--card) / 0.35);
    }
    .dark .rbc-show-more {
      color: hsl(var(--primary));
      background-color: hsl(var(--primary) / 0.12);
      border-radius: 9999px;
    }
  `;

  return (
    <div className={cn('space-y-4', className)}>
      <style>{calendarStyles}</style>
      
      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 text-sm">
        <span className="text-muted-foreground">Priority:</span>
        {(['low', 'medium', 'high', 'urgent'] as TaskPriority[]).map((p) => (
          <div key={p} className="flex items-center gap-1.5">
            <div 
              className="w-3 h-3 rounded-full" 
              style={{ backgroundColor: priorityColors[p] }}
            />
            <span className="capitalize">{p}</span>
          </div>
        ))}
      </div>

      <Card>
        <CardContent className="p-4">
          <BigCalendar<CalendarEvent>
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            style={{ height: 600 }}
            view={view}
            onView={setView}
            date={date}
            onNavigate={handleNavigate}
            onSelectEvent={handleSelectEvent}
            onSelectSlot={handleSelectSlot}
            selectable
            components={{
              event: CustomEvent,
              toolbar: CustomToolbar,
            }}
            eventPropGetter={(event) => ({
              style: {
                backgroundColor: 'transparent',
                border: 'none',
              },
            })}
            dayPropGetter={(date) => {
              const isToday = new Date().toDateString() === date.toDateString();
              return {
                className: cn(
                  isToday && 'bg-[#2B7FFF]/5'
                ),
              };
            }}
          />
        </CardContent>
      </Card>
    </div>
  );
}

export default TaskCalendar;
